import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Upload,
  Phone,
  Mail,
  MapPin,
  Star,
  Briefcase,
  DollarSign,
  Calendar,
  Tag,
  Building2,
  Home,
  X,
  ChevronRight,
  StickyNote,
  Wrench,
  History,
  FileText,
  Receipt,
  CircleDollarSign,
  Pencil,
  Trash2,
  Check,
  Loader2,
  CheckSquare,
  Square,
  User,
  Camera,
  Image as ImageIcon,
  ImagePlus,
  Paperclip,
  Activity as ActivityIcon,
  Crown,
  ClipboardCheck,
  HelpCircle,
  Type as TypeIcon,
  Send,
} from "lucide-react";
import { customerStatusColors } from "../data/customerMock";
import { customersApi, jobsApi, estimatesApi, invoicesApi, tasksApi, usersApi, fileUrl } from "../lib/api";
import TaskDetailModal, { taskAssigneeNames } from "../components/TaskDetailModal";
import { PhotoCaptureButton } from "../components/PhotoCaptureButton";
import { useAuth } from "../contexts/AuthContext";
import { DocDetail, CreateDialog } from "./Estimates";
import NewCustomerModal from "../components/NewCustomerModal";
import NewJobModal from "../components/NewJobModal";
import EmailDocModal from "../components/EmailDocModal";
import ImportWizard from "../components/imports/ImportWizard";
import { SmsConsentWidget } from "../components/SmsConsentWidget";

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "dispatcher";
  const isAdmin = user?.role === "admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [tagFilter, setTagFilter] = useState("All");
  const [sortBy, setSortBy] = useState("recent");
  const [selected, setSelected] = useState(null);
  const [overlayEstimate, setOverlayEstimate] = useState(null);
  const [overlayInvoice, setOverlayInvoice] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([customersApi.list(), jobsApi.list(), estimatesApi.list(), invoicesApi.list()])
      .then(([cs, js, es, ivs]) => {
        if (cancelled) return;
        setCustomers(cs);
        setJobs(js);
        setEstimates(es);
        setInvoices(ivs);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load customers");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Deep-link support: `/customers?id=<id>` auto-opens that customer's drawer.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || loading) return;
    const match = customers.find((c) => c.id === id);
    if (!match) return;
    let active = true;
    (async () => {
      await Promise.resolve();
      if (active) setSelected(match);
    })();
    return () => { active = false; };
  }, [searchParams, loading, customers]);

  const reloadCustomers = () =>
    customersApi.list().then(setCustomers).catch(() => {});

  const reloadJobs = () =>
    jobsApi.list().then(setJobs).catch(() => {});

  const createEstimateForAppointment = async (customer, entry) => {
    try {
      const created = await estimatesApi.create({
        customerId: customer.id,
        // entry.id from CustomerHistoryItem corresponds to a job id (e.g., J-10434)
        jobId: entry.id && /^J-/i.test(entry.id) ? entry.id : null,
        title: `${entry.type} — ${customer.name}`,
        lineItems: [],
      });
      setEstimates((prev) => [created, ...prev]);
      setOverlayEstimate(created);
      toast.success(`Estimate ${created.id} created`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create estimate");
    }
  };

  const allTags = useMemo(() => {
    const s = new Set();
    customers.forEach((c) => (c.tags || []).forEach((t) => s.add(t)));
    return ["All", ...Array.from(s)];
  }, [customers]);

  const filtered = useMemo(() => {
    let list = customers;
    if (typeFilter !== "All") list = list.filter((c) => c.type === typeFilter);
    if (tagFilter !== "All") list = list.filter((c) => (c.tags || []).includes(tagFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q) ||
          c.address.toLowerCase().includes(q)
      );
    }
    if (sortBy === "recent") list = [...list].sort((a, b) => (b.lastService || "").localeCompare(a.lastService || ""));
    else if (sortBy === "value") list = [...list].sort((a, b) => b.lifetimeValue - a.lifetimeValue);
    else if (sortBy === "name") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === "balance") list = [...list].sort((a, b) => b.openBalance - a.openBalance);
    return list;
  }, [customers, search, typeFilter, tagFilter, sortBy]);

  const stats = useMemo(() => {
    const totalLTV = customers.reduce((s, c) => s + c.lifetimeValue, 0);
    const totalOpen = customers.reduce((s, c) => s + c.openBalance, 0);
    const memberCount = customers.filter((c) => (c.tags || []).includes("Membership")).length;
    const commercialCount = customers.filter((c) => c.type === "Commercial").length;
    return { totalLTV, totalOpen, memberCount, commercialCount };
  }, [customers]);

  usePageHeader({
    title: "Customers",
    subtitle: `${customers.length} customers · $${(stats.totalLTV / 1000).toFixed(0)}K lifetime value`,
    actions:
      (canCreate || isAdmin) && (
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              onClick={() => setShowImport(true)}
              data-testid="import-customers-btn"
              variant="outline"
              className="h-9 text-[13px] border-slate-200"
            >
              <Upload className="h-4 w-4 mr-1.5" /> Import
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => setShowNew(true)}
              data-testid="new-customer-btn"
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[13px]"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New Customer
            </Button>
          )}
        </div>
      ),
  });

  return (
    <>
      <ImportWizard
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => reloadCustomers()}
      />

      <NewCustomerModal
        open={showNew || !!editCustomer}
        editCustomer={editCustomer}
        onClose={() => {
          setShowNew(false);
          setEditCustomer(null);
        }}
        existingCustomers={customers}
        existingTags={allTags}
        onSaved={(saved, isEdit) => {
          if (isEdit) {
            setCustomers((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
          } else {
            setCustomers((prev) => [saved, ...prev]);
          }
          setSelected(saved);
        }}
      />

      <div className="p-6 space-y-4">
        {/* KPI bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KpiCard icon={Briefcase} label="Total Customers" value={customers.length} accent="#2563EB" />
          <KpiCard icon={DollarSign} label="Lifetime Revenue" value={`$${(stats.totalLTV / 1000).toFixed(1)}K`} accent="#10B981" />
          <KpiCard icon={CircleDollarSign} label="Open Balance" value={`$${stats.totalOpen.toLocaleString()}`} accent="#F59E0B" />
          <KpiCard icon={Star} label="Members" value={`${stats.memberCount} / ${customers.length}`} accent="#8B5CF6" />
        </div>

        {/* Search + filter bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, email, or address..."
              className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-200 bg-slate-50 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white"
            />
          </div>
          <FilterPills label="Type" options={["All", "Residential", "Commercial"]} value={typeFilter} onChange={setTypeFilter} />
          <FilterPills label="Tag" options={allTags} value={tagFilter} onChange={setTagFilter} />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-9 px-2.5 pr-7 rounded-md border border-slate-200 bg-white text-[12.5px] cursor-pointer hover:border-slate-300"
          >
            <option value="recent">Most Recent</option>
            <option value="value">Lifetime Value</option>
            <option value="name">Name (A–Z)</option>
            <option value="balance">Open Balance</option>
          </select>
        </div>

        {/* Customer table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left font-semibold px-5 py-3">Customer</th>
                  <th className="text-left font-semibold py-3">Contact</th>
                  <th className="text-left font-semibold py-3">Address</th>
                  <th className="text-left font-semibold py-3">Tags</th>
                  <th className="text-right font-semibold py-3">Jobs</th>
                  <th className="text-right font-semibold py-3">LTV</th>
                  <th className="text-right font-semibold py-3">Balance</th>
                  <th className="text-right font-semibold px-5 py-3">Last Service</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-12 text-center text-slate-400 text-[13px]">
                      No customers match these filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((c) => (
                    <tr
                      key={c.id}
                      onClick={() => setSelected(c)}
                      className="border-b border-slate-100 last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-9 w-9 rounded-lg ${c.type === "Commercial" ? "bg-violet-100" : "bg-blue-100"} flex items-center justify-center shrink-0`}>
                            {c.type === "Commercial" ? (
                              <Building2 className="h-4.5 w-4.5 text-violet-600" />
                            ) : (
                              <Home className="h-4.5 w-4.5 text-blue-600" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5 truncate">
                              {c.name}
                              {c.tags.includes("VIP") && (
                                <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500">{c.type} · since {new Date(c.since).getFullYear()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="text-slate-700 text-[12.5px]">{c.phone}</div>
                        <div className="text-[11px] text-slate-500 truncate max-w-[200px]">{c.email}</div>
                      </td>
                      <td className="py-3 text-[12px] text-slate-700">
                        <div className="truncate max-w-[220px]">{c.address}</div>
                        <div className="text-[10.5px] text-slate-500">{c.city}, {c.state} {c.zip}</div>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-wrap gap-1">
                          {c.tags.length === 0 && <span className="text-[11px] text-slate-300">—</span>}
                          {c.tags.map((t) => (
                            <span
                              key={t}
                              className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 text-right font-semibold text-slate-800">{c.jobsCount}</td>
                      <td className="py-3 text-right font-bold text-emerald-700">${c.lifetimeValue.toLocaleString()}</td>
                      <td className="py-3 text-right">
                        {c.openBalance > 0 ? (
                          <span className="font-semibold text-amber-700">${c.openBalance.toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-300">$0</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right text-[11.5px] text-slate-500">
                        {new Date(c.lastService).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <CustomerDetail
          customer={selected}
          canEdit={canCreate}
          onEdit={() => setEditCustomer(selected)}
          jobs={jobs}
          estimates={estimates}
          invoices={invoices}
          onClose={() => {
            setSelected(null);
            if (searchParams.get("id")) {
              const next = new URLSearchParams(searchParams);
              next.delete("id");
              setSearchParams(next, { replace: true });
            }
          }}
          onUpdate={(updated) => {
            setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            setSelected(updated);
          }}
          onCreateEstimateForAppointment={(entry) => createEstimateForAppointment(selected, entry)}
          onOpenEstimate={(est) => setOverlayEstimate(est)}
          onOpenInvoice={(inv) => setOverlayInvoice(inv)}
          onOpenJob={(jobId) => navigate(`/jobs?id=${encodeURIComponent(jobId)}`)}
          onJobCreated={(job) => {
            reloadJobs();
            window.dispatchEvent(new CustomEvent("job-created", { detail: job }));
          }}
          onEstimateCreated={(doc) => {
            setEstimates((prev) => [doc, ...prev]);
            setOverlayEstimate(doc);
          }}
        />
      )}
      {overlayEstimate && (
        <DocDetail
          kind="estimate"
          doc={overlayEstimate}
          customers={customers}
          jobs={jobs}
          onClose={() => setOverlayEstimate(null)}
          onUpdated={(updated) => {
            setEstimates((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            setOverlayEstimate(updated);
          }}
          onDeleted={(id) => {
            setEstimates((prev) => prev.filter((e) => e.id !== id));
            setOverlayEstimate(null);
          }}
          onConverted={(newInvoice) => {
            estimatesApi.get(overlayEstimate.id).then((e) => {
              setEstimates((prev) => prev.map((x) => (x.id === e.id ? e : x)));
            });
            setInvoices((prev) => [newInvoice, ...prev]);
            toast.success(`Invoice ${newInvoice.id} created`);
            setOverlayEstimate(null);
            setOverlayInvoice(newInvoice);
          }}
        />
      )}
      {overlayInvoice && (
        <DocDetail
          kind="invoice"
          doc={overlayInvoice}
          customers={customers}
          jobs={jobs}
          onClose={() => setOverlayInvoice(null)}
          onUpdated={(updated) => {
            setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
            setOverlayInvoice(updated);
          }}
          onDeleted={(id) => {
            setInvoices((prev) => prev.filter((i) => i.id !== id));
            setOverlayInvoice(null);
          }}
          onConverted={() => {}}
        />
      )}
    </>
  );
}

function KpiCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="mt-1.5 text-[24px] font-bold text-slate-900 leading-none">{value}</div>
        </div>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}18` }}>
          <Icon className="h-5 w-5" style={{ color: accent }} />
        </div>
      </div>
    </div>
  );
}

function FilterPills({ label, options, value, onChange }) {
  return (
    <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg p-1 border border-slate-200">
      <span className="text-[10.5px] uppercase font-semibold text-slate-400 tracking-wider px-1.5">{label}</span>
      {options.slice(0, 5).map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={`h-7 px-2.5 rounded-md text-[11.5px] font-medium transition-colors ${
            value === o ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function CustomerDetail({ customer, canEdit = false, onEdit, jobs = [], estimates = [], invoices = [], onClose, onUpdate, onCreateEstimateForAppointment, onOpenEstimate, onOpenInvoice, onOpenJob, onJobCreated, onEstimateCreated }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [composer, setComposer] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [viewingNote, setViewingNote] = useState(null);
  const [customerTasks, setCustomerTasks] = useState([]);
  const [openTask, setOpenTask] = useState(null);
  const [staffUsers, setStaffUsers] = useState([]);
  const [tasksCollapsed, setTasksCollapsed] = useState(false);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null); // invoice being emailed a pay link from the Payments tab

  const canCreateJob = user?.role === "admin" || user?.role === "dispatcher";

  const canManageTasks = user?.role === "admin" || user?.role === "dispatcher";

  // Load tasks linked to this customer (shown as a TASKS section at the top of notes).
  useEffect(() => {
    let active = true;
    tasksApi.list({ customerId: customer.id }).then((t) => { if (active) setCustomerTasks(t); }).catch(() => {});
    return () => { active = false; };
  }, [customer.id]);

  // Staff list for the assignee picker inside the task modal (admin/dispatcher only).
  useEffect(() => {
    if (!canManageTasks) return;
    let active = true;
    usersApi.list().then((u) => { if (active) setStaffUsers(u.map((x) => ({ id: x.id, name: x.name || x.email }))); }).catch(() => {});
    return () => { active = false; };
  }, [canManageTasks]);

  const refreshCustomerTasks = async () => {
    try { setCustomerTasks(await tasksApi.list({ customerId: customer.id })); } catch { /* non-critical */ }
  };
  const refreshCustomer = async () => {
    try { const c = await customersApi.get(customer.id); onUpdate && onUpdate(c); } catch { /* non-critical */ }
  };
  const openTasksCount = customerTasks.filter((t) => t.status !== "done").length;

  const isAdmin = user?.role === "admin";
  const myEmail = user?.email || "";

  // Filter the global jobs list to just this customer's jobs — used for the
  // Activity feed so job created / status / completion events surface here.
  const customerJobs = useMemo(() => {
    return (jobs || []).filter((j) => j.customerId === customer.id);
  }, [jobs, customer.id]);

  // Filter the global estimates/invoices lists to this customer (sorted newest first).
  const customerEstimates = useMemo(() => {
    return (estimates || [])
      .filter((e) => e.customerId === customer.id)
      .sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime());
  }, [estimates, customer.id]);

  const customerInvoices = useMemo(() => {
    return (invoices || [])
      .filter((i) => i.customerId === customer.id)
      .sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime());
  }, [invoices, customer.id]);

  // Payments tab: flatten every invoice's payments[] into a single newest-first
  // list, each carrying the job/estimate/invoice it's attached to for linking.
  const customerPayments = useMemo(() => {
    const out = [];
    for (const inv of customerInvoices) {
      const estimate = inv.estimateId ? estimates.find((e) => e.id === inv.estimateId) : null;
      for (const p of inv.payments || []) {
        out.push({
          id: p.id,
          invoiceId: inv.id,
          invoice: inv,
          jobId: inv.jobId || null,
          estimate,
          amount: p.amount,
          receivedAt: p.receivedAt,
        });
      }
    }
    return out.sort((a, b) => new Date(b.receivedAt) - new Date(a.receivedAt));
  }, [customerInvoices, estimates]);

  // Open Balances: any invoice still owing money — carries whichever of
  // job/estimate/invoice are attached so each can be linked from the row.
  const customerOpenBalances = useMemo(() => {
    return customerInvoices
      .filter((inv) => (inv.balanceDue || 0) > 0 && inv.status !== "Voided")
      .map((inv) => ({
        invoiceId: inv.id,
        invoice: inv,
        jobId: inv.jobId || null,
        estimate: inv.estimateId ? estimates.find((e) => e.id === inv.estimateId) : null,
        balanceDue: inv.balanceDue,
      }))
      .sort((a, b) => (b.balanceDue || 0) - (a.balanceDue || 0));
  }, [customerInvoices, estimates]);

  // Build a unified, newest-first feed combining manual notes + live jobs + legacy history.
  // Jobs without a scheduled start time bubble to the top of the feed as TBD.
  const feed = useMemo(() => {
    const TBD_SENTINEL = Number.MAX_SAFE_INTEGER;
    const noteEntries = (customer.notes || []).map((n) => ({
      kind: "note",
      id: n.id,
      text: n.text,
      createdAt: n.createdAt,
      createdBy: n.createdBy,
      updatedAt: n.updatedAt,
      history: n.history || [],
      source: n.source || null,
      sortKey: new Date(n.createdAt).getTime() || 0,
      canModify: isAdmin || n.createdBy === myEmail,
    }));
    // Live jobs for this customer — derived from the jobs collection, so status
    // changes reflect immediately on the next reload.
    const jobEntries = (jobs || [])
      .filter((j) => j.customerId === customer.id)
      .map((j) => {
        const t = j.start ? new Date(j.start).getTime() : NaN;
        const hasDate = !Number.isNaN(t);
        return {
          kind: "job",
          id: j.id,
          jobId: j.id,
          type: j.type,
          status: j.status || "Scheduled",
          tech: j.tech,
          value: j.value,
          createdAt: hasDate ? j.start : null,
          // TBD jobs (no start date) sort to the very top of the feed.
          sortKey: hasDate ? t : TBD_SENTINEL,
          tbd: !hasDate,
        };
      });
    // Legacy seeded history (customer.history) — kept per user spec (1c).
    const apptEntries = (customer.history || []).map((h) => ({
      kind: "appointment",
      id: h.id,
      type: h.type,
      status: h.status,
      tech: h.tech,
      value: h.value,
      createdAt: h.date && h.date.length === 10 ? `${h.date}T00:00:00Z` : h.date,
      sortKey: new Date(h.date && h.date.length === 10 ? `${h.date}T00:00:00Z` : h.date).getTime() || 0,
    }));
    const all = [...noteEntries, ...jobEntries, ...apptEntries];
    all.sort((a, b) => b.sortKey - a.sortKey);
    return all;
  }, [customer.notes, customer.history, customer.id, jobs, isAdmin, myEmail]);

  const addNote = async () => {
    const text = composer.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const updated = await customersApi.addNote(customer.id, text);
      onUpdate(updated);
      setComposer("");
    } catch (err) {
      toast.error("Failed to add note");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const beginEdit = (n) => {
    setEditingId(n.id);
    setEditingText(n.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText("");
  };

  const saveEdit = async () => {
    const text = editingText.trim();
    if (!text || !editingId || busy) return;
    setBusy(true);
    try {
      const updated = await customersApi.updateNote(customer.id, editingId, text);
      onUpdate(updated);
      cancelEdit();
    } catch (err) {
      toast.error("Failed to update note");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  const removeNote = async (noteId) => {
    if (busy) return;
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    setBusy(true);
    try {
      const updated = await customersApi.removeNote(customer.id, noteId);
      onUpdate(updated);
    } catch (err) {
      toast.error("Failed to delete note");
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  // ----- Photos -----
  const photos = customer.photos || [];
  const [photoBusy, setPhotoBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const uploadPhotos = async (files) => {
    if (!files || !files.length || photoBusy) return;
    const count = files.length;
    setPhotoBusy(true);
    try {
      const updated = await customersApi.uploadPhotos(customer.id, files);
      onUpdate(updated);
      toast.success(`${count} photo${count === 1 ? "" : "s"} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo? This cannot be undone.")) return;
    try {
      const updated = await customersApi.deletePhoto(customer.id, photoId);
      onUpdate(updated);
      setLightbox(null);
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  const TABS = [
    { k: "overview", label: "Overview", icon: StickyNote },
    { k: "activity", label: "Activity", icon: ActivityIcon, count: (customer.activity || []).length },
    { k: "photos", label: "Photos", icon: ImageIcon, count: photos.length },
    { k: "estimates", label: "Estimates", icon: FileText, count: customerEstimates.length },
    { k: "invoices", label: "Invoices", icon: Receipt, count: customerInvoices.length },
    { k: "payments", label: "Payments", icon: CircleDollarSign, count: customerPayments.length },
    { k: "checklist", label: "Check List", icon: ClipboardCheck, count: (customer.checklists || []).length },
    { k: "history", label: "Service History", icon: History, count: (customer.serviceHistory || []).length + customer.history.length },
    { k: "properties", label: "Properties", icon: Building2, count: customer.properties.length },
    { k: "equipment", label: "Equipment", icon: Wrench, count: customer.equipment.length + (customer.materialsUsed || []).length },
    { k: "notes", label: "Notes", icon: StickyNote, count: (customer.notes || []).length },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-detail-modal"
        className="relative w-full h-full sm:w-[1000px] sm:max-w-[96vw] sm:h-[90vh] bg-white sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`h-12 w-12 rounded-xl ${customer.type === "Commercial" ? "bg-violet-100" : "bg-blue-100"} flex items-center justify-center shrink-0`}>
              {customer.type === "Commercial" ? (
                <Building2 className="h-6 w-6 text-violet-600" />
              ) : (
                <Home className="h-6 w-6 text-blue-600" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h2 className="text-[18px] font-bold text-slate-900 truncate">{customer.name}</h2>
                {customer.tags.includes("VIP") && <Star className="h-4 w-4 text-amber-500 fill-amber-400" />}
                {customer.membershipInfo && customer.membershipInfo.status === "active" && (
                  <span
                    data-testid="customer-membership-badge"
                    className="inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-0.5 rounded-full text-white"
                    style={{ background: "#7C3AED" }}
                    title={customer.membershipInfo.expiresAt ? `Expires ${new Date(customer.membershipInfo.expiresAt).toLocaleDateString()}` : "Lifetime member"}
                  >
                    <Crown className="h-2.5 w-2.5" />
                    {customer.membershipInfo.planName} Member
                  </span>
                )}
              </div>
              <div className="text-[12px] text-slate-500 flex items-center gap-1.5">
                <span>{customer.type}</span>
                <span>·</span>
                <span>Customer since {new Date(customer.since).toLocaleDateString([], { month: "short", year: "numeric" })}</span>
                {customer.membershipInfo && customer.membershipInfo.status === "active" && (
                  <>
                    <span>·</span>
                    <span className="text-violet-600 font-medium">
                      {customer.membershipInfo.discountPct}% member discount
                      {customer.membershipInfo.expiresAt ? ` · exp ${new Date(customer.membershipInfo.expiresAt).toLocaleDateString([], { month: "short", year: "numeric" })}` : " · lifetime"}
                    </span>
                  </>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {customer.tags.map((t) => (
                  <span key={t} className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                    <Tag className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5" />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {canEdit && onEdit && (
              <Button
                variant="outline"
                onClick={onEdit}
                data-testid="customer-edit-btn"
                className="h-8 text-[12.5px] px-3"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
            )}
            <button onClick={onClose} data-testid="customer-close-btn" className="h-11 w-11 rounded-md hover:bg-slate-100 flex items-center justify-center">
              <X className="h-7 w-7" style={{ color: "#ffffff" }} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Body: left tab rail + content */}
        <div className="flex-1 flex min-h-0">
          {/* Left tab rail */}
          <nav className="w-[132px] sm:w-[208px] shrink-0 border-r border-slate-100 bg-slate-50/50 overflow-y-auto py-2" data-testid="customer-tab-rail">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.k;
              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`customer-tab-${t.k}`}
                  className={`w-full flex items-center gap-2.5 px-3 sm:px-4 py-2.5 text-left border-l-[3px] transition-colors group ${
                    active ? "border-blue-600 bg-white text-blue-700" : "border-transparent text-slate-600 hover:!bg-blue-600/20 hover:!text-white"
                  }`}
                >
                  <Icon className={`h-4 w-4 shrink-0 ${active ? "text-blue-600" : "text-slate-400 group-hover:!text-white"}`} />
                  <span className={`text-[12px] sm:text-[13px] font-semibold truncate flex-1 ${active ? "" : "group-hover:!text-white"}`}>{t.label}</span>
                  {typeof t.count === "number" && t.count > 0 && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-500 group-hover:!bg-white/20 group-hover:!text-white"}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Content column */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Stats + contact header */}
            <div className="border-b border-slate-100 shrink-0">
              <div className="grid grid-cols-4 bg-slate-50/40">
                <Stat label="Lifetime Value" value={`$${customer.lifetimeValue.toLocaleString()}`} accent="text-emerald-700" />
                <Stat label="Open Balance" value={customer.openBalance > 0 ? `$${customer.openBalance.toLocaleString()}` : "$0"} accent={customer.openBalance > 0 ? "text-amber-700" : "text-slate-700"} />
                <Stat label="Total Jobs" value={customer.jobsCount} accent="text-slate-900" />
                <Stat label="Rating" value={customer.rating ? "★".repeat(customer.rating) : "—"} accent="text-amber-500" />
              </div>
              <div className="px-5 py-2.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12.5px]">
                <a href={`tel:${customer.phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                  <Phone className="h-3.5 w-3.5 shrink-0" /> {customer.phone}
                </a>
                <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-blue-600 hover:underline truncate">
                  <Mail className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{customer.email}</span>
                </a>
                <div className="sm:col-span-2 flex items-start gap-2 text-slate-700">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <span>{customer.address}, {customer.city}, {customer.state} {customer.zip}</span>
                </div>
                <div className="sm:col-span-2">
                  <SmsConsentWidget customerId={customer.id} />
                </div>
              </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="customer-tab-content">
          {tab === "overview" && (
            <div className="space-y-4">
              {customerTasks.length > 0 && (
                <div data-testid="customer-tasks-section">
                  <button
                    type="button"
                    onClick={() => setTasksCollapsed((v) => !v)}
                    data-testid="customer-tasks-toggle"
                    className="w-full flex items-center justify-between mb-1.5 group"
                  >
                    <div className="flex items-center gap-1.5">
                      <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform ${tasksCollapsed ? "" : "rotate-90"}`} />
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 group-hover:text-slate-700">Tasks</span>
                    </div>
                    <div className="text-[10.5px] text-slate-400">{openTasksCount} open · {customerTasks.length} total</div>
                  </button>
                  {!tasksCollapsed && (
                    <div className="space-y-2">
                      {customerTasks.map((t) => {
                        const isDone = t.status === "done";
                        const names = taskAssigneeNames(t);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setOpenTask(t)}
                            data-testid={`customer-task-${t.id}`}
                            className={`w-full text-left rounded-lg border-2 px-3 py-2.5 bg-white hover:shadow-sm transition-all ${isDone ? "border-emerald-300" : "border-rose-300"}`}
                          >
                            <div className="flex items-center gap-2">
                              {isDone ? <CheckSquare className="h-4 w-4 text-emerald-600 shrink-0" /> : <Square className="h-4 w-4 text-rose-400 shrink-0" />}
                              <span className={`text-[13px] font-semibold truncate ${isDone ? "text-slate-400 line-through" : "text-slate-900"}`}>{t.title}</span>
                              <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${isDone ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{isDone ? "Completed" : "Open"}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-slate-500 pl-6">
                              {names.length > 0 && (
                                <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`}</span>
                              )}
                              {t.dueAt && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(t.dueAt.length === 10 ? `${t.dueAt}T00:00:00` : t.dueAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
                              {Array.isArray(t.noteLog) && t.noteLog.length > 0 && <span className="inline-flex items-center gap-1 text-blue-600"><StickyNote className="h-3 w-3" /> {t.noteLog.length}</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <RecapCard label="Last Service" value={customer.lastService ? new Date(customer.lastService).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—"} icon={Calendar} />
                <RecapCard label="Member Since" value={customer.since ? new Date(customer.since).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—"} icon={Calendar} />
              </div>
            </div>
          )}

          {tab === "photos" && (
            <PhotosPanel photos={photos} onUpload={uploadPhotos} onOpen={setLightbox} busy={photoBusy} />
          )}

          {tab === "notes" && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Notes &amp; Appointments</div>
                <div className="text-[10.5px] text-slate-400">{feed.length} {feed.length === 1 ? "entry" : "entries"} · newest first</div>
              </div>

              {/* Composer */}
              <div className="rounded-md border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400">
                <textarea
                  rows={2}
                  data-testid="customer-note-composer"
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  placeholder="Add a note — access codes, preferences, follow-ups…"
                  className="w-full px-3 py-2 text-[13px] resize-none focus:outline-none rounded-md"
                />
                <div className="flex items-center justify-between px-2 pb-2">
                  <div className="text-[10.5px] text-slate-400 px-1">
                    Stamped automatically as <strong className="text-slate-600">{myEmail || "you"}</strong> · now
                  </div>
                  <Button
                    size="sm"
                    data-testid="customer-note-submit"
                    onClick={addNote}
                    disabled={!composer.trim() || busy}
                    className="h-7 text-[12px] bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-500"
                    style={{ color: "#ffffff" }}
                  >
                    {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" color="#ffffff" /> : <Plus className="h-3 w-3 mr-1" color="#ffffff" />}
                    <span style={{ color: "#ffffff" }}>Add Note</span>
                  </Button>
                </div>
              </div>

              {/* Feed */}
              <div
                data-testid="customer-notes-feed"
                className="mt-3 max-h-[372px] overflow-y-auto rounded-md border border-slate-200 bg-slate-50/60 divide-y divide-slate-200"
              >
                {feed.length === 0 ? (
                  <div className="text-center py-10 text-[12.5px] text-slate-400 italic">
                    No notes or appointments yet — add the first one above.
                  </div>
                ) : (
                  feed.map((entry) => (
                    <FeedEntry
                      key={`${entry.kind}-${entry.id}`}
                      entry={entry}
                      editing={editingId === entry.id}
                      editingText={editingText}
                      setEditingText={setEditingText}
                      beginEdit={beginEdit}
                      cancelEdit={cancelEdit}
                      saveEdit={saveEdit}
                      removeNote={removeNote}
                      busy={busy}
                      onCreateEstimateForAppointment={onCreateEstimateForAppointment}
                      onOpenJob={onOpenJob}
                      onOpenNote={(e) => setViewingNote(e)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {tab === "history" && (
            <div className="space-y-2 max-h-[372px] overflow-y-auto pr-1" data-testid="customer-history-list">
              {(customer.serviceHistory || []).map((h) => {
                const sc = customerStatusColors[h.status] || customerStatusColors.Completed;
                const when = h.completedAt || h.paymentDate || h.date;
                return (
                  <div key={h.id} data-testid={`customer-service-history-${h.id}`} className="border border-slate-200 rounded-md px-3 py-2.5 hover:border-blue-300 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                          {h.jobId ? (
                            <button
                              type="button"
                              onClick={() => onOpenJob && onOpenJob(h.jobId)}
                              data-testid={`customer-service-history-job-${h.jobId}`}
                              className="text-blue-600 hover:underline"
                            >
                              {h.jobId}
                            </button>
                          ) : (
                            <span>{h.invoiceNumber || h.invoiceId}</span>
                          )}
                          <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.text }}>
                            {h.status}
                          </span>
                          {h.invoiceId && (
                            <button
                              type="button"
                              onClick={() => {
                                const inv = (customerInvoices || []).find((i) => i.id === h.invoiceId);
                                if (inv && onOpenInvoice) onOpenInvoice(inv);
                              }}
                              data-testid={`customer-service-history-invoice-${h.invoiceId}`}
                              className="text-[10.5px] font-semibold text-emerald-700 hover:underline"
                            >
                              {h.invoiceNumber || h.invoiceId}
                            </button>
                          )}
                        </div>
                        <div className="text-[11.5px] text-slate-600 truncate">{h.type}</div>
                        <div className="text-[10.5px] text-slate-500 mt-0.5">
                          {when && new Date(when).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                          {h.tech && h.tech !== "—" && <> · {h.tech}</>}
                          {h.paymentMethod && (
                            <> · Paid by <span className="uppercase font-semibold text-slate-700">{h.paymentMethod}</span>
                              {h.paymentDate && <> on {new Date(h.paymentDate).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</>}
                            </>
                          )}
                        </div>
                        {h.notes && (
                          <div className="mt-1.5 text-[11.5px] text-slate-600 whitespace-pre-wrap line-clamp-3">{h.notes}</div>
                        )}
                      </div>
                      <div className="font-bold text-emerald-700 text-[13px] shrink-0">${(h.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                );
              })}
              {/* Legacy seeded appointments (customer.history) stay visible so demo tenants keep their sample data. */}
              {customer.history.map((h) => {
                const sc = customerStatusColors[h.status] || customerStatusColors.Completed;
                return (
                  <div key={h.id} className="border border-slate-200 rounded-md px-3 py-2.5 hover:border-blue-300 hover:shadow-sm transition-all flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
                        {h.id}
                        <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: sc.bg, color: sc.text }}>
                          {h.status}
                        </span>
                      </div>
                      <div className="text-[11.5px] text-slate-600 truncate">{h.type}</div>
                      <div className="text-[10.5px] text-slate-500">
                        {new Date(h.date).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })} · {h.tech}
                      </div>
                    </div>
                    <div className="font-bold text-emerald-700 text-[13px] shrink-0">${h.value.toLocaleString()}</div>
                  </div>
                );
              })}
              {(customer.serviceHistory || []).length === 0 && customer.history.length === 0 && <Empty text="No service history yet." />}
            </div>
          )}

          {tab === "estimates" && (
            <div className="space-y-2" data-testid="customer-estimates-list">
              {customerEstimates.length === 0 && <Empty text="No estimates on file yet. Use the “Estimate” button on a past appointment or the Estimates & Invoices page to create one." />}
              {customerEstimates.map((e) => (
                <button
                  key={e.id}
                  onClick={() => onOpenEstimate && onOpenEstimate(e)}
                  data-testid={`customer-estimate-${e.id}`}
                  className="w-full text-left border border-slate-200 rounded-md px-3 py-2.5 hover:border-blue-300 hover:shadow-sm transition-all flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 text-blue-600" />
                      {e.id}
                      <span className="inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {e.status}
                      </span>
                      {e.convertedInvoiceId && (
                        <span className="text-[10.5px] font-semibold text-violet-700">→ {e.convertedInvoiceId}</span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-slate-600 truncate">{e.title || <span className="italic text-slate-400">(no title)</span>}</div>
                    <div className="text-[10.5px] text-slate-500">
                      Issued {new Date(e.issuedAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      {e.jobId && <> · linked to <span className="font-mono">{e.jobId}</span></>}
                    </div>
                  </div>
                  <div className="font-bold text-emerald-700 text-[14px] shrink-0">${(e.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                </button>
              ))}
            </div>
          )}

          {tab === "invoices" && (
            <div className="space-y-2 max-h-[372px] overflow-y-auto pr-1" data-testid="customer-invoices-list">
              {customerInvoices.length === 0 && <Empty text="No invoices on file yet." />}
              {customerInvoices.map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => onOpenInvoice && onOpenInvoice(inv)}
                  data-testid={`customer-invoice-${inv.id}`}
                  className="w-full text-left border border-slate-200 rounded-md px-3 py-2.5 hover:border-blue-300 hover:shadow-sm transition-all flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
                      <Receipt className="h-3.5 w-3.5 text-emerald-600" />
                      {inv.id}
                      <span className={`inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                        inv.status === "Paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        inv.status === "Partially Paid" ? "bg-amber-50 text-amber-700 border-amber-200" :
                        inv.status === "Overdue" ? "bg-rose-50 text-rose-700 border-rose-200" :
                        inv.status === "Voided" ? "bg-slate-100 text-slate-500 border-slate-200 line-through" :
                        "bg-slate-50 text-slate-700 border-slate-200"
                      }`}>
                        {inv.status}
                      </span>
                      {inv.estimateId && (
                        <span className="text-[10.5px] font-semibold text-blue-700">from {inv.estimateId}</span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-slate-600 truncate">{inv.title || <span className="italic text-slate-400">(no title)</span>}</div>
                    <div className="text-[10.5px] text-slate-500">
                      Issued {new Date(inv.issuedAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
                      {inv.jobId && <> · linked to <span className="font-mono">{inv.jobId}</span></>}
                      {inv.dueAt && <> · due {new Date(inv.dueAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="font-bold text-slate-900 text-[14px]">${(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    {(inv.balanceDue || 0) > 0 && (
                      <div className="text-[10.5px] font-semibold text-amber-700">Balance ${inv.balanceDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {tab === "payments" && (
            <div className="space-y-5" data-testid="customer-payments-panel">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Payment History</div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-[13px]" data-testid="customer-payment-history-table">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                      <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                        <th className="px-4 py-2.5 w-24">Job #</th>
                        <th className="px-4 py-2.5 w-28">Estimate #</th>
                        <th className="px-4 py-2.5 w-28">Invoice #</th>
                        <th className="px-4 py-2.5">Date &amp; Time</th>
                        <th className="px-4 py-2.5 w-32 text-right">Amount Paid</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {customerPayments.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 italic">No payments recorded yet.</td></tr>
                      ) : customerPayments.map((p) => (
                        <tr key={p.id} data-testid={`customer-payment-row-${p.id}`}>
                          <td className="px-4 py-2.5">
                            {p.jobId ? (
                              <button type="button" onClick={() => onOpenJob && onOpenJob(p.jobId)} data-testid={`customer-payment-job-${p.id}`} className="text-blue-600 hover:underline font-semibold">{p.jobId}</button>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            {p.estimate ? (
                              <button type="button" onClick={() => onOpenEstimate && onOpenEstimate(p.estimate)} data-testid={`customer-payment-estimate-${p.id}`} className="text-blue-600 hover:underline font-semibold">{p.estimate.id}</button>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            <button type="button" onClick={() => onOpenInvoice && onOpenInvoice(p.invoice)} data-testid={`customer-payment-invoice-${p.id}`} className="text-blue-600 hover:underline font-semibold">{p.invoiceId}</button>
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{formatStamp(p.receivedAt)}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-emerald-700">${Number(p.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Open Balances</div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full text-[13px]" data-testid="customer-open-balances-table">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                      <tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                        <th className="px-4 py-2.5 w-28 text-right">Amount Due</th>
                        <th className="px-4 py-2.5 w-24">Job #</th>
                        <th className="px-4 py-2.5 w-28">Estimate #</th>
                        <th className="px-4 py-2.5 w-28">Invoice #</th>
                        <th className="px-4 py-2.5 w-24"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {customerOpenBalances.length === 0 ? (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 italic">No open balances — all paid up.</td></tr>
                      ) : customerOpenBalances.map((b) => (
                        <tr key={b.invoiceId} data-testid={`customer-open-balance-row-${b.invoiceId}`}>
                          <td className="px-4 py-2.5 text-right font-bold text-amber-700">${Number(b.balanceDue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-2.5">
                            {b.jobId ? (
                              <button type="button" onClick={() => onOpenJob && onOpenJob(b.jobId)} data-testid={`customer-open-balance-job-${b.invoiceId}`} className="text-blue-600 hover:underline font-semibold">{b.jobId}</button>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            {b.estimate ? (
                              <button type="button" onClick={() => onOpenEstimate && onOpenEstimate(b.estimate)} data-testid={`customer-open-balance-estimate-${b.invoiceId}`} className="text-blue-600 hover:underline font-semibold">{b.estimate.id}</button>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="px-4 py-2.5">
                            <button type="button" onClick={() => onOpenInvoice && onOpenInvoice(b.invoice)} data-testid={`customer-open-balance-invoice-${b.invoiceId}`} className="text-blue-600 hover:underline font-semibold">{b.invoiceId}</button>
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="outline"
                              className="h-7 text-[11.5px] px-2.5"
                              data-testid={`customer-open-balance-send-${b.invoiceId}`}
                              onClick={() => setEmailTarget(b.invoice)}
                            >
                              <Send className="h-3 w-3 mr-1" /> Send
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {tab === "properties" && (
            <div className="space-y-2 max-h-[372px] overflow-y-auto pr-1" data-testid="customer-properties-list">
              {customer.properties.map((p) => (
                <div key={p.id} className="border border-slate-200 rounded-md p-3 hover:border-blue-300 transition-all">
                  <div className="flex items-start gap-2">
                    <Building2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-slate-900">{p.label}</div>
                      <div className="text-[11.5px] text-slate-500">{p.address}</div>
                      <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-600">
                        <span>{p.sqft.toLocaleString()} sq ft</span>
                        <span>·</span>
                        <span>{p.units} unit{p.units !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "equipment" && (
            <div className="space-y-4 max-h-[372px] overflow-y-auto pr-1" data-testid="customer-equipment-list">
              {(customer.equipment.length > 0 || (customer.materialsUsed || []).length === 0) && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Serviced Equipment</div>
                  <div className="space-y-2">
                    {customer.equipment.map((e) => (
                      <div key={e.id} data-testid={`customer-equipment-${e.id}`} className="border border-slate-200 rounded-md p-3 hover:border-blue-300 transition-all">
                        <div className="flex items-start gap-2">
                          <Wrench className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-slate-900">{e.type}</div>
                            {(e.brand || e.model) && (
                              <div className="text-[11.5px] text-slate-600">{e.brand} {e.model}</div>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                              {e.serial && <><span className="font-mono">SN: {e.serial}</span><span>·</span></>}
                              {e.installedAt && <>
                                <span>Installed {new Date(e.installedAt).toLocaleDateString([], { month: "short", year: "numeric" })}</span>
                                <span>·</span>
                              </>}
                              {e.warranty && (
                                <span className={`font-semibold ${e.warranty === "Expired" ? "text-red-600" : "text-emerald-700"}`}>
                                  Warranty: {e.warranty}
                                </span>
                              )}
                              {e.jobId && (
                                <>
                                  <span>·</span>
                                  <button
                                    type="button"
                                    onClick={() => onOpenJob && onOpenJob(e.jobId)}
                                    className="text-blue-600 hover:underline font-semibold"
                                    data-testid={`customer-equipment-job-${e.jobId}`}
                                  >
                                    {e.jobId}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {customer.equipment.length === 0 && <Empty text="No equipment on file." />}
                  </div>
                </div>
              )}

              {(customer.materialsUsed || []).length > 0 && (
                <div data-testid="customer-materials-used">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5 mt-2">Parts &amp; Materials History</div>
                  <div className="space-y-2">
                    {(customer.materialsUsed || []).map((m) => (
                      <div key={m.id} data-testid={`customer-material-${m.id}`} className="border border-slate-200 rounded-md p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-semibold text-slate-900 truncate">{m.name}</div>
                            {m.description && m.description !== m.name && (
                              <div className="text-[11.5px] text-slate-600 truncate">{m.description}</div>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                              <span>{m.qty} {m.unit || "each"}</span>
                              {m.sku && <><span>·</span><span className="font-mono">SKU {m.sku}</span></>}
                              {m.tech && <><span>·</span><span>{m.tech}</span></>}
                              {m.usedAt && <><span>·</span><span>{new Date(m.usedAt).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}</span></>}
                              {m.jobId && (
                                <>
                                  <span>·</span>
                                  <button
                                    type="button"
                                    onClick={() => onOpenJob && onOpenJob(m.jobId)}
                                    className="text-blue-600 hover:underline font-semibold"
                                    data-testid={`customer-material-job-${m.jobId}`}
                                  >
                                    {m.jobId}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[13px] font-bold text-slate-900">${((m.qty || 0) * (m.unitPrice || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-[10.5px] text-slate-500">${(m.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / {m.unit || "each"}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "activity" && (
            <ActivityPanel
              customer={customer}
              jobs={customerJobs}
              estimates={customerEstimates}
              invoices={customerInvoices}
              onOpenJob={onOpenJob}
              onOpenEstimate={onOpenEstimate}
              onOpenInvoice={onOpenInvoice}
              onGoToNotes={() => setTab("notes")}
            />
          )}
          {tab === "checklist" && (
            <ChecklistPanel checklists={customer.checklists || []} />
          )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 sm:px-6 py-3 bg-white flex items-center gap-2">
          <Button variant="outline" className="h-9 text-[12.5px]" onClick={() => (window.location.href = `tel:${customer.phone.replace(/[^0-9]/g, "")}`)}>
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Call
          </Button>
          <Button variant="outline" className="h-9 text-[12.5px]" onClick={() => (window.location.href = `mailto:${customer.email}`)}>
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Email
          </Button>
          <Button variant="outline" className="h-9 text-[12.5px]" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${customer.address}, ${customer.city}, ${customer.state} ${customer.zip}`)}`, "_blank", "noopener")}>
            <MapPin className="h-3.5 w-3.5 mr-1.5" /> Map
          </Button>
          <div className="flex-1" />
          {canCreateJob && (
            <Button
              className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px]"
              data-testid="customer-new-estimate-btn"
              onClick={() => setEstimateOpen(true)}
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Create Estimate
            </Button>
          )}
          {canCreateJob && (
            <Button
              className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[12.5px]"
              data-testid="customer-new-job-btn"
              onClick={() => setNewJobOpen(true)}
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New Job
            </Button>
          )}
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NewJobModal
          open={newJobOpen}
          prefillCustomer={customer}
          onClose={() => setNewJobOpen(false)}
          onCreated={(job) => {
            setNewJobOpen(false);
            onJobCreated && onJobCreated(job);
          }}
        />
      </div>

      {estimateOpen && (
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <CreateDialog
            kind="estimate"
            customers={[customer]}
            jobs={jobs}
            prefillCustomerId={customer.id}
            onClose={() => setEstimateOpen(false)}
            onCreated={(doc) => {
              setEstimateOpen(false);
              onEstimateCreated && onEstimateCreated(doc);
            }}
          />
        </div>
      )}

      {/* Photo lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4"
          onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          data-testid="customer-photo-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="customer-photo-lightbox-close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full">
            <img
              src={fileUrl(lightbox.url)}
              alt={lightbox.caption || lightbox.filename || "Customer photo"}
              className="w-full max-h-[78vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-[14px] font-medium truncate">{lightbox.caption || lightbox.filename}</div>
                <div className="text-white/50 text-[11.5px]">
                  {(lightbox.createdAt || "").slice(0, 10)}{lightbox.uploadedBy ? ` · ${lightbox.uploadedBy}` : ""}
                </div>
              </div>
              {canEdit && (
                <Button
                  onClick={() => deletePhoto(lightbox.id)}
                  data-testid="customer-photo-delete"
                  className="h-9 text-[13px] bg-white/10 border border-white/20 text-white hover:bg-rose-600 hover:border-rose-600 shrink-0"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {viewingNote && (
        <NoteDetailModal note={viewingNote} onClose={() => setViewingNote(null)} />
      )}

      {openTask && (
        <TaskDetailModal
          task={openTask}
          users={staffUsers}
          canManage={canManageTasks}
          canNote={canManageTasks || (user && (openTask.assigneeIds || []).includes(user.id))}
          onClose={() => setOpenTask(null)}
          onUpdated={async (updated) => {
            setOpenTask(updated);
            setCustomerTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            window.dispatchEvent(new Event("tasks-changed"));
            await refreshCustomer();
            await refreshCustomerTasks();
          }}
        />
      )}
      {emailTarget && (
        <EmailDocModal
          kind="invoice"
          doc={emailTarget}
          customer={customer}
          payLink={`${window.location.origin}/pay/${emailTarget.id}`}
          onClose={() => setEmailTarget(null)}
          onSent={refreshCustomer}
        />
      )}
    </div>
  );
}

function NoteDetailModal({ note, onClose }) {
  const history = (note.history || []).slice().reverse();
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200 max-h-[85vh] flex flex-col" data-testid="note-detail-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
            <StickyNote className="h-4 w-4 text-amber-500" /> Note details
          </div>
          <button onClick={onClose} data-testid="note-detail-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4 text-slate-500" /></button>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 mb-1.5">
              <span>{formatStamp(note.createdAt)}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-medium truncate">{note.createdBy}</span>
              {note.source === "task" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">from task</span>}
              {note.updatedAt && <span className="text-slate-400 italic text-[10px]">(edited)</span>}
            </div>
            <div className="text-[13.5px] text-slate-800 whitespace-pre-wrap break-words" data-testid="note-detail-text">{note.text}</div>
          </div>

          {history.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10.5px] uppercase tracking-wide font-bold text-slate-400 mb-2">Edit history · {history.length}</div>
              <div className="space-y-2" data-testid="note-detail-history">
                {history.map((h, i) => (
                  <div key={i} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2">
                    <div className="flex items-center gap-1.5 text-[10.5px] text-slate-500 mb-1">
                      <span>{formatStamp(h.editedAt)}</span>
                      {h.editedBy && (<><span className="text-slate-300">·</span><span className="truncate">{h.editedBy}</span></>)}
                    </div>
                    <div className="text-[12.5px] text-slate-600 whitespace-pre-wrap break-words line-through decoration-slate-300">{h.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ACTIVITY_META = {
  form_submitted:    { color: "bg-blue-100 text-blue-600",       Icon: FileText },
  customer_created:  { color: "bg-emerald-100 text-emerald-600", Icon: User },
  pipeline_added:    { color: "bg-violet-100 text-violet-600",   Icon: Briefcase },
  assigned:          { color: "bg-amber-100 text-amber-600",     Icon: User },
  stage_moved:       { color: "bg-indigo-100 text-indigo-600",   Icon: ChevronRight },
  job_created:       { color: "bg-blue-100 text-blue-700",       Icon: Briefcase },
  job_status:        { color: "bg-indigo-100 text-indigo-700",   Icon: ActivityIcon },
  job_completed:     { color: "bg-emerald-100 text-emerald-700", Icon: CheckSquare },
  payment:           { color: "bg-emerald-100 text-emerald-700", Icon: CircleDollarSign },
  note:              { color: "bg-yellow-100 text-yellow-700",   Icon: StickyNote },
  estimate_created:  { color: "bg-sky-100 text-sky-700",         Icon: FileText },
  estimate_accepted: { color: "bg-emerald-100 text-emerald-700", Icon: CheckSquare },
  invoice_created:   { color: "bg-blue-100 text-blue-700",       Icon: Receipt },
  invoice_paid:      { color: "bg-emerald-100 text-emerald-700", Icon: CircleDollarSign },
};

function ActivityPanel({
  customer,
  jobs = [],
  estimates = [],
  invoices = [],
  onOpenJob,
  onOpenEstimate,
  onOpenInvoice,
  onGoToNotes,
}) {
  const items = useMemo(() => {
    const out = [];

    // 1) Seeded lead activity (form_submitted, customer_created, etc.)
    for (const a of customer.activity || []) {
      out.push({
        id: `act-${a.id}`,
        type: a.type,
        message: a.message,
        createdAt: a.createdAt,
        by: a.by,
      });
    }

    // 2) Job created + status-change events pulled from every job's log[].
    for (const j of jobs) {
      for (const l of j.log || []) {
        const t = l.time;
        if (!t) continue;
        const isCreated = l.type === "created";
        const isCompleted = l.type === "finished";
        out.push({
          id: `job-${j.id}-${l.id}`,
          type: isCreated ? "job_created" : (isCompleted ? "job_completed" : "job_status"),
          message: isCreated
            ? `Job ${j.id} created · ${j.type || "Service"}`
            : `${j.id} · ${l.label}`,
          createdAt: t,
          by: l.by,
          onClick: () => onOpenJob && onOpenJob(j.id),
        });
      }
    }

    // 3) Payments from every invoice's payments[].
    for (const inv of invoices) {
      for (const p of inv.payments || []) {
        out.push({
          id: `pay-${p.id}`,
          type: "payment",
          message: `Payment received · $${Number(p.amount || 0).toLocaleString()} via ${p.method}${p.reference ? ` (${p.reference})` : ""} on ${inv.id}`,
          createdAt: p.receivedAt,
          by: p.recordedBy,
          onClick: () => onOpenInvoice && onOpenInvoice(inv),
        });
      }
    }

    // 4) Manual notes.
    for (const n of customer.notes || []) {
      const preview = (n.text || "").length > 120 ? `${n.text.slice(0, 120)}…` : n.text;
      out.push({
        id: `note-${n.id}`,
        type: "note",
        message: `Note added · ${preview}`,
        createdAt: n.createdAt,
        by: n.createdBy,
        onClick: () => onGoToNotes && onGoToNotes(),
      });
    }

    // 5) Estimates — created + accepted milestones.
    for (const e of estimates) {
      if (e.issuedAt) {
        out.push({
          id: `est-${e.id}-issued`,
          type: "estimate_created",
          message: `Estimate ${e.id} created · $${Number(e.total || 0).toLocaleString()}`,
          createdAt: e.issuedAt,
          onClick: () => onOpenEstimate && onOpenEstimate(e),
        });
      }
      if (e.acceptedAt) {
        out.push({
          id: `est-${e.id}-accepted`,
          type: "estimate_accepted",
          message: `Estimate ${e.id} accepted`,
          createdAt: e.acceptedAt,
          onClick: () => onOpenEstimate && onOpenEstimate(e),
        });
      }
    }

    // 6) Invoices — created + fully-paid milestones.
    for (const inv of invoices) {
      if (inv.issuedAt) {
        out.push({
          id: `inv-${inv.id}-issued`,
          type: "invoice_created",
          message: `Invoice ${inv.id} created · $${Number(inv.total || 0).toLocaleString()}`,
          createdAt: inv.issuedAt,
          onClick: () => onOpenInvoice && onOpenInvoice(inv),
        });
      }
      if (inv.status === "Paid") {
        // Anchor "invoice paid" at the most recent payment timestamp if present.
        const lastPay = (inv.payments || []).slice().sort(
          (a, b) => new Date(b.receivedAt) - new Date(a.receivedAt),
        )[0];
        const paidAt = lastPay ? lastPay.receivedAt : inv.issuedAt;
        if (paidAt) {
          out.push({
            id: `inv-${inv.id}-paid`,
            type: "invoice_paid",
            message: `Invoice ${inv.id} paid in full`,
            createdAt: paidAt,
            onClick: () => onOpenInvoice && onOpenInvoice(inv),
          });
        }
      }
    }

    // Newest first.
    out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return out;
  }, [customer, jobs, estimates, invoices, onOpenJob, onOpenEstimate, onOpenInvoice, onGoToNotes]);

  if (items.length === 0) {
    return (
      <div className="text-center py-14 rounded-xl border-2 border-dashed border-slate-200 text-slate-400" data-testid="customer-activity-empty">
        <ActivityIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        <div className="text-[13px] font-medium text-slate-500">No activity yet</div>
        <div className="text-[12px]">Job, payment, estimate, invoice and note activity for this customer will appear here.</div>
      </div>
    );
  }
  return (
    <div className="space-y-1" data-testid="customer-activity-list">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Lead Activity Timeline</div>
      <ol className="relative border-l border-slate-200 ml-3">
        {items.map((a) => {
          const meta = ACTIVITY_META[a.type] || { color: "bg-slate-100 text-slate-500", Icon: ActivityIcon };
          const Icon = meta.Icon;
          const clickable = typeof a.onClick === "function";
          const RowTag = clickable ? "button" : "div";
          return (
            <li key={a.id} className="ml-5 mb-4" data-testid={`customer-activity-${a.id}`}>
              <span className={`absolute -left-[13px] flex items-center justify-center h-6 w-6 rounded-full ring-4 ring-white ${meta.color}`}>
                <Icon className="h-3 w-3" />
              </span>
              <RowTag
                onClick={clickable ? a.onClick : undefined}
                className={`block w-full text-left rounded-md ${clickable ? "hover:bg-slate-50 cursor-pointer transition-colors -mx-2 px-2 py-1" : ""}`}
                data-testid={`customer-activity-row-${a.id}`}
              >
                <div className={`text-[13px] font-medium ${clickable ? "text-blue-700 hover:underline" : "text-slate-800"}`}>
                  {a.message}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {new Date(a.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  {a.by && a.by !== "system" ? ` · ${a.by}` : ""}
                </div>
              </RowTag>
            </li>
          );
        })}
      </ol>
    </div>
  );
}


// "Check List" tab — completed job-completion checklists mirrored from the field.
const ANSWER_ICON = {
  question: HelpCircle,
  input: TypeIcon,
  action: Wrench,
  diagnosis: ActivityIcon,
  recommend: Receipt,
};

function ChecklistPanel({ checklists }) {
  const items = [...(checklists || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  if (items.length === 0) {
    return (
      <div className="text-center py-14 rounded-xl border-2 border-dashed border-slate-200 text-slate-400" data-testid="customer-checklist-empty">
        <ClipboardCheck className="h-8 w-8 mx-auto mb-2 text-slate-300" />
        <div className="text-[13px] font-medium text-slate-500">No checklists yet</div>
        <div className="text-[12px]">Completed job-completion checklists from the field will appear here.</div>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="customer-checklist-list">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Completed Checklists</div>
      {items.map((cl) => (
        <div key={cl.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`customer-checklist-${cl.id}`}>
          <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-bold text-slate-900 flex items-center gap-1.5">
                <ClipboardCheck className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                {cl.flowName || "Checklist"}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {cl.jobTitle ? `${cl.jobTitle} · ` : ""}
                <span className="font-mono">{cl.jobId}</span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10.5px] text-slate-500">{formatStamp(cl.date)}</div>
              {cl.by && <div className="text-[10.5px] text-slate-400">by {cl.by}</div>}
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {(cl.answers || []).map((a, i) => {
              const Icon = ANSWER_ICON[a.type] || HelpCircle;
              return (
                <div key={`${cl.id}-${i}`} className="px-3.5 py-2.5 flex items-start gap-2.5">
                  <span className="h-6 w-6 rounded-md bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="h-3.5 w-3.5 text-slate-500" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-slate-700 flex items-center gap-1.5">
                      {a.question}
                      {a.required && (
                        <span className="text-[8.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700">Required</span>
                      )}
                    </div>
                    <div className="text-[12.5px] text-slate-900 mt-0.5 break-words">{a.answer || <span className="italic text-slate-400">—</span>}</div>
                  </div>
                </div>
              );
            })}
            {(cl.answers || []).length === 0 && (
              <div className="px-3.5 py-3 text-[12px] text-slate-400 italic">No recorded answers.</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function PhotosPanel({ photos, onUpload, onOpen, busy }) {
  return (
    <div className="space-y-3" data-testid="customer-photos-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          Photos{photos.length > 0 ? ` · ${photos.length}` : ""}
        </div>
        <PhotoCaptureButton onFiles={onUpload} busy={busy} label="Add photo" testid="customer-photo-add" />
      </div>
      {photos.length === 0 ? (
        <div className="text-center py-14 rounded-xl border-2 border-dashed border-slate-200 text-slate-400" data-testid="customer-photos-empty">
          <ImageIcon className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <div className="text-[13px] font-medium text-slate-500">No photos yet</div>
          <div className="text-[12px]">Use “Add photo” to take a picture or upload from your gallery.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="customer-photos-grid">
          {photos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpen(p)}
              data-testid={`customer-photo-${p.id}`}
              className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 hover:ring-2 hover:ring-blue-400 transition"
            >
              <img
                src={fileUrl(p.url)}
                alt={p.caption || p.filename || "Customer photo"}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
              />
              {p.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-left">
                  <span className="text-[10.5px] text-white font-medium line-clamp-1">{p.caption}</span>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="px-5 py-3 border-r border-slate-100 last:border-r-0">
      <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className={`mt-0.5 text-[16px] font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function RecapCard({ label, value, icon: Icon }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 flex items-center gap-2.5">
      <div className="h-8 w-8 rounded-md bg-slate-50 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className="text-[13px] font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-8 text-[12.5px] text-slate-400">{text}</div>;
}

// Timestamp formatter — per user spec: "Mon, Jul 7, 2025 · 10:30 AM"
function formatStamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return `${date} · ${time}`;
}

function FeedEntry({ entry, editing, editingText, setEditingText, beginEdit, cancelEdit, saveEdit, removeNote, busy, onCreateEstimateForAppointment, onOpenJob, onOpenNote }) {
  if (entry.kind === "job") {
    const statusColor =
      entry.status === "Completed" ? "bg-emerald-50 border-emerald-200 text-emerald-700"
      : entry.status === "In Progress" || entry.status === "On Site" ? "bg-amber-50 border-amber-200 text-amber-700"
      : entry.status === "Canceled" ? "bg-rose-50 border-rose-200 text-rose-700"
      : "bg-white border-blue-200 text-blue-700";
    return (
      <div data-testid={`feed-job-${entry.id}`} className="px-3 py-2.5 bg-blue-50/40">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 min-w-0">
            <Calendar className="h-3 w-3 shrink-0" />
            <span className="truncate">{entry.tbd ? "TBD — no date set" : formatStamp(entry.createdAt)}</span>
            <span className="text-blue-400">·</span>
            <span className="uppercase tracking-wide text-[10px] text-blue-600">Appointment</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${statusColor}`}>
              {entry.status || "Scheduled"}
            </span>
            {onOpenJob && (
              <button
                onClick={() => onOpenJob(entry.jobId)}
                data-testid={`feed-job-open-${entry.id}`}
                title={`Open ${entry.jobId}`}
                className="h-6 px-1.5 rounded border border-blue-200 bg-white hover:bg-blue-50 hover:border-blue-300 flex items-center gap-1 text-[10.5px] font-semibold text-blue-700 transition-colors"
              >
                <Briefcase className="h-3 w-3" />
                {entry.jobId}
              </button>
            )}
          </div>
        </div>
        <div className="mt-1 text-[13px] text-slate-800">
          <span className="font-semibold">{entry.type}</span>
          {entry.tech && entry.tech !== "Unassigned" && (
            <span className="text-slate-500"> · Tech: {entry.tech}</span>
          )}
          {typeof entry.value === "number" && entry.value > 0 && (
            <span className="text-emerald-700 font-semibold"> · ${entry.value.toLocaleString()}</span>
          )}
        </div>
      </div>
    );
  }

  if (entry.kind === "appointment") {
    return (
      <div data-testid={`feed-appt-${entry.id}`} className="px-3 py-2.5 bg-blue-50/40">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-700">
            <Calendar className="h-3 w-3" />
            <span>{formatStamp(entry.createdAt)}</span>
            <span className="text-blue-400">·</span>
            <span className="uppercase tracking-wide text-[10px] text-blue-600">Appointment</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white border border-blue-200 text-blue-700">
              {entry.status || "Scheduled"}
            </span>
            {onCreateEstimateForAppointment && (
              <button
                onClick={() => onCreateEstimateForAppointment(entry)}
                data-testid={`feed-appt-estimate-${entry.id}`}
                title="Create estimate from this appointment"
                className="h-6 px-1.5 rounded border border-blue-200 bg-white hover:bg-blue-50 hover:border-blue-300 flex items-center gap-1 text-[10.5px] font-semibold text-blue-700 transition-colors"
              >
                <FileText className="h-3 w-3" />
                Estimate
              </button>
            )}
          </div>
        </div>
        <div className="mt-1 text-[13px] text-slate-800">
          <span className="font-semibold">{entry.type}</span>
          {entry.tech && <span className="text-slate-500"> · Tech: {entry.tech}</span>}
          {typeof entry.value === "number" && entry.value > 0 && (
            <span className="text-emerald-700 font-semibold"> · ${entry.value.toLocaleString()}</span>
          )}
        </div>
      </div>
    );
  }

  // note
  return (
    <div data-testid={`feed-note-${entry.id}`} className="px-3 py-2.5 bg-white">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
          <StickyNote className="h-3 w-3 text-amber-500" />
          <span>{formatStamp(entry.createdAt)}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 font-medium">{entry.createdBy}</span>
          {entry.updatedAt && (
            <span className="text-slate-400 italic text-[10px]">(edited)</span>
          )}
        </div>
        {entry.canModify && !editing && (
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => beginEdit(entry)}
              data-testid={`feed-note-edit-${entry.id}`}
              disabled={busy}
              className="h-6 w-6 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 disabled:opacity-50"
              title="Edit note"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={() => removeNote(entry.id)}
              data-testid={`feed-note-delete-${entry.id}`}
              disabled={busy}
              className="h-6 w-6 rounded hover:bg-red-50 flex items-center justify-center text-slate-500 hover:text-red-600 disabled:opacity-50"
              title="Delete note"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-1.5 space-y-1.5">
          <textarea
            rows={3}
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            data-testid={`feed-note-edit-textarea-${entry.id}`}
            className="w-full px-2 py-1.5 rounded border border-blue-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
            autoFocus
          />
          <div className="flex items-center justify-end gap-1.5">
            <Button size="sm" variant="outline" onClick={cancelEdit} disabled={busy} className="h-7 text-[11.5px]">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={saveEdit}
              disabled={busy || !editingText.trim()}
              data-testid={`feed-note-edit-save-${entry.id}`}
              className="h-7 text-[11.5px] bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Check className="h-3 w-3 mr-1" /> Save
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => onOpenNote && onOpenNote(entry)}
          data-testid={`feed-note-open-${entry.id}`}
          title="Click to view full note"
          className="mt-1 w-full text-left text-[13px] text-slate-800 whitespace-pre-wrap break-words line-clamp-2 hover:text-blue-700 cursor-pointer transition-colors"
        >
          {entry.text}
        </button>
      )}
    </div>
  );
}
